import { useEffect, useState, useCallback } from "react";
import { Plus, Pencil, Trash2, KeyRound, ShieldCheck, Clock } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Checkbox } from "../components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { api, errMsg } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Card } from "../components/Card";
import { DataTable, FilterSelect } from "../components/DataTable";

const UserDialog = ({ value, roles, groups, onClose, onSaved }) => {
  const { t } = useI18n();
  const [u, setU] = useState(value);
  useEffect(() => setU(value), [value]);
  if (!u) return null;
  const toggleGroup = (id) => setU({ ...u, groups: u.groups.includes(id) ? u.groups.filter((g) => g !== id) : [...u.groups, id] });
  const save = async () => {
    try {
      if (u.id) await api.patch(`/users/${u.id}`, { name: u.name, role: u.is_default_admin ? undefined : u.role, groups: u.groups, active: u.active, language: u.language, ...(u.password ? { password: u.password } : {}) });
      else await api.post("/users", u);
      toast.success(t("saved")); onSaved();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="user-dialog">
        <DialogHeader><DialogTitle>{u.id ? t("edit") : t("addUser")}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5"><Label>{t("name")}</Label><Input data-testid="user-name-input" value={u.name} onChange={(e) => setU({ ...u, name: e.target.value })} className="rounded-xl" /></div>
          {!u.id && <div className="space-y-1.5"><Label>{t("email")}</Label><Input data-testid="user-email-input" dir="ltr" type="email" value={u.email} onChange={(e) => setU({ ...u, email: e.target.value })} className="rounded-xl" /></div>}
          <div className="space-y-1.5"><Label>{t("password")}</Label><Input data-testid="user-password-input" dir="ltr" type="password" value={u.password || ""} onChange={(e) => setU({ ...u, password: e.target.value })} className="rounded-xl" placeholder={u.id ? "••••••" : "min 10"} /></div>
          {!u.is_default_admin && <div className="space-y-1.5"><Label>{t("role")}</Label><FilterSelect value={u.role} onChange={(v) => setU({ ...u, role: v })} options={roles.filter((r) => r !== "PLATFORM_SUPER_ADMIN")} placeholder={t("role")} testId="user-role-select" allowAll={false} /></div>}
          <div className="space-y-2"><Label>{t("groups")}</Label>
            {groups.map((g) => <label key={g.id} className="flex items-center gap-2 text-sm"><Checkbox data-testid={`user-group-${g.id}`} checked={u.groups.includes(g.id)} onCheckedChange={() => toggleGroup(g.id)} />{g.name}</label>)}
          </div>
          {u.id && !u.is_default_admin && <label className="flex items-center gap-2 text-sm"><Switch data-testid="user-active-switch" checked={u.active} onCheckedChange={(v) => setU({ ...u, active: v })} />{t("active")}</label>}
        </div>
        <DialogFooter><Button data-testid="user-save-button" onClick={save} className="rounded-xl bg-slate-900">{t("save")}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const GroupDialog = ({ value, onClose, onSaved }) => {
  const { t } = useI18n();
  const [g, setG] = useState(value);
  const save = async () => {
    try { if (g.id) await api.patch(`/groups/${g.id}`, { name: g.name, description: g.description }); else await api.post("/groups", g); toast.success(t("saved")); onSaved(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="group-dialog">
        <DialogHeader><DialogTitle>{g.id ? t("edit") : t("addGroup")}</DialogTitle></DialogHeader>
        <Input data-testid="group-name-input" placeholder={t("name")} value={g.name} onChange={(e) => setG({ ...g, name: e.target.value })} className="rounded-xl" />
        <Input data-testid="group-description-input" placeholder={t("description")} value={g.description} onChange={(e) => setG({ ...g, description: e.target.value })} className="rounded-xl" />
        <DialogFooter><Button data-testid="group-save-button" onClick={save} className="rounded-xl bg-slate-900">{t("save")}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function UserManager() {
  const { t, tv } = useI18n();
  const { isAdmin } = useAuth();
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [roles, setRoles] = useState([]);
  const [editU, setEditU] = useState(null);
  const [editG, setEditG] = useState(null);
  const load = useCallback(() => {
    api.get("/users").then((r) => setUsers(r.data));
    api.get("/groups").then((r) => setGroups(r.data));
  }, []);
  useEffect(() => { load(); api.get("/roles").then((r) => setRoles(r.data)); }, [load]);
  const act = async (fn) => { try { await fn(); toast.success(t("saved")); load(); } catch (e) { toast.error(errMsg(e)); } };
  const gname = (id) => groups.find((g) => g.id === id)?.name || id;

  const cols = [
    { key: "name", label: t("name"), render: (r) => <div><p className="font-medium text-slate-900">{r.name}</p><bdi className="text-xs text-slate-500">{r.email}</bdi></div> },
    { key: "role", label: t("role"), render: (r) => <span className="font-mono text-xs">{r.role}{r.is_default_admin && <span className="ms-1 rounded bg-sky-100 px-1 text-sky-800">{t("defaultAdmin")}</span>}</span> },
    { key: "groups", label: t("groups"), render: (r) => <span className="text-xs">{r.groups.map(gname).join(", ")}</span> },
    { key: "mfa", label: t("mfa"), render: (r) => r.mfa_enrolled ? <span className="inline-flex items-center gap-1 text-xs text-emerald-800"><ShieldCheck className="h-3.5 w-3.5" />{t("enrolled")}</span> : <span className="inline-flex items-center gap-1 text-xs text-amber-800"><Clock className="h-3.5 w-3.5" />{t("pending")}</span> },
    { key: "active", label: t("status"), render: (r) => tv(r.active ? "active" : "disabled") },
    ...(isAdmin ? [{ key: "act", label: t("actions"), render: (r) => (
      <div className="flex gap-1">
        <Button size="icon" variant="ghost" data-testid={`edit-user-${r.email}`} onClick={() => setEditU({ ...r, password: "" })} aria-label={t("edit")}><Pencil className="h-4 w-4" /></Button>
        <Button size="icon" variant="ghost" data-testid={`reset-mfa-${r.email}`} onClick={() => act(() => api.post(`/users/${r.id}/reset-mfa`))} aria-label={t("resetMfa")} title={t("resetMfa")}><KeyRound className="h-4 w-4" /></Button>
        {!r.is_default_admin && <Button size="icon" variant="ghost" data-testid={`delete-user-${r.email}`} onClick={() => act(() => api.delete(`/users/${r.id}`))} aria-label={t("delete")}><Trash2 className="h-4 w-4 text-rose-700" /></Button>}
      </div>) }] : []),
  ];

  return (
    <div>
      <PageHeader title={t("nav_users")} sub={`${users.length} ${t("users")} · ${groups.length} ${t("groups")}`} />
      <Tabs defaultValue="users">
        <TabsList className="mb-4 rounded-xl"><TabsTrigger data-testid="tab-users" value="users">{t("users")}</TabsTrigger><TabsTrigger data-testid="tab-groups" value="groups">{t("groups")}</TabsTrigger></TabsList>
        <TabsContent value="users">
          {isAdmin && <Button data-testid="add-user-button" onClick={() => setEditU({ name: "", email: "", password: "", role: "VIEWER", groups: [], language: "en" })} className="mb-4 rounded-xl bg-slate-900"><Plus className="h-4 w-4" />{t("addUser")}</Button>}
          <DataTable cols={cols} rows={users} testId="users-table" />
        </TabsContent>
        <TabsContent value="groups">
          {isAdmin && <Button data-testid="add-group-button" onClick={() => setEditG({ name: "", description: "" })} className="mb-4 rounded-xl bg-slate-900"><Plus className="h-4 w-4" />{t("addGroup")}</Button>}
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="groups-grid">
            {groups.map((g) => (
              <Card key={g.id} data-testid={`group-card-${g.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div><p className="font-semibold text-slate-900">{g.name}</p><p className="mt-1 text-sm text-slate-500">{g.description}</p></div>
                  {isAdmin && <div className="flex">
                    <Button size="icon" variant="ghost" data-testid={`edit-group-${g.id}`} onClick={() => setEditG(g)}><Pencil className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" data-testid={`delete-group-${g.id}`} onClick={() => act(() => api.delete(`/groups/${g.id}`))}><Trash2 className="h-4 w-4 text-rose-700" /></Button>
                  </div>}
                </div>
                <p className="mt-4 text-xs font-semibold uppercase tracking-wider text-slate-500">{t("members")}: {g.member_count}</p>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>
      {editU && <UserDialog value={editU} roles={roles} groups={groups} onClose={() => setEditU(null)} onSaved={() => { setEditU(null); load(); }} />}
      {editG && <GroupDialog value={editG} onClose={() => setEditG(null)} onSaved={() => { setEditG(null); load(); }} />}
    </div>
  );
}
